import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';

const PasswordChangeForm = () => {
  const { updatePassword, loading: contextLoading, error: contextError, success: contextSuccess } = useSettings();


  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync context errors to local form errors
  useEffect(() => {
    if (contextError) setErrors({ submit: contextError });
    if (contextSuccess) setErrors({});
  }, [contextError, contextSuccess]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const user = JSON.parse(localStorage.getItem('user'));
    await updatePassword({
      old_password: formData.currentPassword,
      new_password: formData.newPassword,
      update_audit_id: user?.id
    });

    if (!contextError) {
      // Clear form if success
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^a-zA-Z\d]/.test(password)) strength++;

    const levels = [
      { label: 'Very Weak', color: 'bg-red-500' },
      { label: 'Weak', color: 'bg-orange-500' },
      { label: 'Fair', color: 'bg-yellow-500' },
      { label: 'Good', color: 'bg-blue-500' },
      { label: 'Strong', color: 'bg-green-500' }
    ];

    return { strength, ...levels[strength] };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-6">
      {/* Current Password */}
      <div className="space-y-2">
        <label
          htmlFor="currentPassword"
          className="flex items-center gap-2 text-base text-black opacity-80 font-medium"
        >
          <Lock className="w-4 h-4 text-teal-600" />
          Current Password
        </label>
        <div className="relative">
          <input
            id="currentPassword"
            type={showPasswords.current ? 'text' : 'password'}
            value={formData.currentPassword}
            onChange={(e) => handleInputChange('currentPassword', e.target.value)}
            placeholder="Enter your current password"
            className={`w-full px-4 py-3 pr-12 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 bg-stone-50 focus:ring-2 transition-colors ${
              errors.currentPassword ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'
            }`}
            aria-describedby={errors.currentPassword ? 'currentPassword-error' : undefined}
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('current')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
            aria-label={showPasswords.current ? 'Hide password' : 'Show password'}
          >
            {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
        {errors.currentPassword && (
          <p id="currentPassword-error" className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.currentPassword}
          </p>
        )}
      </div>

      {/* New Password */}
      <div className="space-y-2">
        <label
          htmlFor="newPassword"
          className="flex items-center gap-2 text-base text-black opacity-80 font-medium"
        >
          <Lock className="w-4 h-4 text-teal-600" />
          New Password
        </label>
        <div className="relative">
          <input
            id="newPassword"
            type={showPasswords.new ? 'text' : 'password'}
            value={formData.newPassword}
            onChange={(e) => handleInputChange('newPassword', e.target.value)}
            placeholder="Enter your new password"
            className={`w-full px-4 py-3 pr-12 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 bg-stone-50 focus:ring-2 transition-colors ${
              errors.newPassword ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'
            }`}
            aria-describedby={errors.newPassword ? 'newPassword-error' : 'newPassword-help'}
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('new')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
            aria-label={showPasswords.new ? 'Hide password' : 'Show password'}
          >
            {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        {/* Password Strength Indicator */}
        {formData.newPassword && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                  style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                />
              </div>
              <span className="text-sm font-medium text-gray-600">{passwordStrength.label}</span>
            </div>
          </div>
        )}

        {errors.newPassword && (
          <p id="newPassword-error" className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.newPassword}
          </p>
        )}

        <p id="newPassword-help" className="text-sm text-gray-600">
          Password must be at least 8 characters with uppercase, lowercase, and numbers
        </p>
      </div>

      {/* Confirm Password */}
      <div className="space-y-2">
        <label
          htmlFor="confirmPassword"
          className="flex items-center gap-2 text-base text-black opacity-80 font-medium"
        >
          <Lock className="w-4 h-4 text-teal-600" />
          Confirm New Password
        </label>
        <div className="relative">
          <input
            id="confirmPassword"
            type={showPasswords.confirm ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            placeholder="Confirm your new password"
            className={`w-full px-4 py-3 pr-12 rounded-lg border-none outline-none text-base text-black placeholder-black placeholder-opacity-40 bg-stone-50 focus:ring-2 transition-colors ${
              errors.confirmPassword ? 'focus:ring-red-500 bg-red-50' : 'focus:ring-teal-500'
            }`}
            aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('confirm')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded"
            aria-label={showPasswords.confirm ? 'Hide password' : 'Show password'}
          >
            {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        {/* Password Match Indicator */}
        {formData.confirmPassword && (
          <div className="flex items-center gap-2">
            {formData.newPassword === formData.confirmPassword ? (
              <div className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm">Passwords match</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-red-600">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">Passwords do not match</span>
              </div>
            )}
          </div>
        )}

        {errors.confirmPassword && (
          <p id="confirmPassword-error" className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.confirmPassword}
          </p>
        )}
      </div>

      {/* Success Alert */}
      {contextSuccess && (
        <div className="p-4 mb-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {contextSuccess}
        </div>
      )}

      {/* Submit Error */}
      {errors.submit && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {errors.submit}
          </p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-2 bg-teal-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-teal-700 hover:shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Changing Password...
          </>
        ) : (
          <>
            <Save className="w-5 h-5" />
            Change Password
          </>
        )}
      </button>
    </form>
  );
};

export default PasswordChangeForm;
